import { useState, useEffect, useRef, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { releaseApi } from '../api'
import type { PodSnapshot, NamespaceSnapshot, DeploymentSnapshot, AiMessage, NodeMetric, HpaInfo } from '../api'
import { PageDescription } from '../components/PageDescription'

// ---- Status helpers ----

const podUptime = (startTime: string) => {
    const secs = Math.floor((Date.now() - new Date(startTime).getTime()) / 1000)
    if (secs < 60) return `${secs}s`
    const mins = Math.floor(secs / 60)
    if (mins < 60) return `${mins}m`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours}h ${mins % 60}m`
    const days = Math.floor(hours / 24)
    return `${days}d ${hours % 24}h`
}

const podStatusColor = (pod: PodSnapshot) => {
    if (!pod.ready && pod.status === 'CrashLoopBackOff') return { dot: 'bg-red-500', ring: 'ring-red-500/40', text: 'text-red-400' }
    if (!pod.ready && pod.phase === 'Pending') return { dot: 'bg-amber-400', ring: 'ring-amber-400/40', text: 'text-amber-400' }
    if (pod.ready && pod.status === 'Running') return { dot: 'bg-emerald-400', ring: 'ring-emerald-400/40', text: 'text-emerald-400' }
    return { dot: 'bg-slate-500', ring: 'ring-slate-500/40', text: 'text-slate-400' }
}

const deployStatus = (dep: DeploymentSnapshot) => {
    if (dep.pods.some(p => !p.ready && p.status === 'CrashLoopBackOff')) return 'error'
    if (dep.readyReplicas < dep.desiredReplicas) return 'degraded'
    if (dep.readyReplicas === dep.desiredReplicas) return 'healthy'
    return 'unknown'
}

const nsStatus = (ns: NamespaceSnapshot) => {
    const statuses = ns.deployments.map(deployStatus)
    if (statuses.includes('error')) return 'error'
    if (statuses.includes('degraded')) return 'degraded'
    if (statuses.every(s => s === 'healthy')) return 'healthy'
    return 'unknown'
}

const STATUS_STYLES = {
    healthy:  { dot: 'bg-emerald-400', badge: 'bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30' },
    degraded: { dot: 'bg-amber-400',   badge: 'bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30' },
    error:    { dot: 'bg-red-500',     badge: 'bg-red-500/15 text-red-400 ring-1 ring-red-500/30' },
    unknown:  { dot: 'bg-slate-500',   badge: 'bg-slate-500/15 text-slate-400 ring-1 ring-slate-500/30' },
}

// ---- Log viewer ----

interface SelectedPod {
    namespace: string
    pod: PodSnapshot
}

function PodLogViewer({ selected, onClose }: { selected: SelectedPod; onClose: () => void }) {
    const [tailLines, setTailLines] = useState(200)
    const [messages, setMessages] = useState<AiMessage[]>([])
    const [input, setInput] = useState('')
    const [showChat, setShowChat] = useState(false)
    const bottomRef = useRef<HTMLDivElement>(null)

    const logsQuery = useQuery({
        queryKey: ['pod-logs', selected.namespace, selected.pod.name, tailLines],
        queryFn: () => releaseApi.getPodLogs(selected.namespace, selected.pod.name, tailLines),
        refetchInterval: 15_000,
    })

    const aiMutation = useMutation({
        mutationFn: ({ msgs }: { msgs: AiMessage[] }) =>
            releaseApi.troubleshoot(logsQuery.data ?? '', msgs),
        onSuccess: (data, { msgs }) => {
            setMessages([...msgs, { role: 'assistant', content: data.reply }])
        },
    })

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [messages])

    const sendMessage = () => {
        if (!input.trim() || aiMutation.isPending) return
        const newMsgs: AiMessage[] = [...messages, { role: 'user', content: input.trim() }]
        setMessages(newMsgs)
        setInput('')
        aiMutation.mutate({ msgs: newMsgs })
    }

    const colors = podStatusColor(selected.pod)

    return (
        <div className="fixed inset-y-0 right-0 z-50 w-[640px] bg-slate-900 border-l border-slate-700 flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 shrink-0">
                <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                        <span className={`h-2 w-2 rounded-full ${colors.dot}`} />
                        <span className="text-sm font-medium text-white font-mono">{selected.pod.name}</span>
                        <span className="text-xs text-slate-500">{selected.namespace}</span>
                    </div>
                    <div className="flex items-center gap-3 pl-4">
                        {selected.pod.image && (
                            <span className="text-[10px] font-mono text-slate-500 truncate">{selected.pod.image.split('/').pop()}</span>
                        )}
                        {selected.pod.startTime && (
                            <span className="text-[10px] text-slate-600 shrink-0">
                                up {podUptime(selected.pod.startTime)}
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <select
                        value={tailLines}
                        onChange={e => setTailLines(Number(e.target.value))}
                        className="text-xs bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-300"
                    >
                        {[50, 100, 200, 500, 1000].map(n => (
                            <option key={n} value={n}>{n} lines</option>
                        ))}
                    </select>
                    <button
                        onClick={() => setShowChat(v => !v)}
                        className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-medium transition-colors ${
                            showChat
                                ? 'bg-violet-600 text-white'
                                : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white'
                        }`}
                    >
                        <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M8 0C3.58 0 0 3.13 0 7c0 2.3 1.22 4.35 3.1 5.65L2.5 16l3.74-1.88C7.05 14.37 7.52 14.4 8 14.4c4.42 0 8-3.13 8-7S12.42 0 8 0z" />
                        </svg>
                        Ask Claude
                    </button>
                    <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
                        <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M2 2l12 12M14 2L2 14" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>
            </div>

            {showChat ? (
                <div className="flex flex-col flex-1 min-h-0">
                    <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                        {messages.length === 0 && (
                            <div className="text-xs text-slate-500 text-center py-6">
                                Ask Claude about this pod. It can see the pod logs above.
                                <br />
                                <span className="text-slate-600">e.g. "Why is this pod crashing?" or "What does this error mean?"</span>
                            </div>
                        )}
                        {messages.map((m, i) => (
                            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[90%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                                    m.role === 'user'
                                        ? 'bg-violet-600 text-white'
                                        : 'bg-slate-800 text-slate-200'
                                }`}>
                                    {m.content}
                                </div>
                            </div>
                        ))}
                        {aiMutation.isPending && (
                            <div className="flex justify-start">
                                <div className="bg-slate-800 rounded-lg px-3 py-2 text-sm text-slate-400 flex items-center gap-1.5">
                                    <span className="animate-pulse">●</span>
                                    <span className="animate-pulse" style={{ animationDelay: '150ms' }}>●</span>
                                    <span className="animate-pulse" style={{ animationDelay: '300ms' }}>●</span>
                                </div>
                            </div>
                        )}
                        {aiMutation.isError && (
                            <p className="text-xs text-red-400 text-center">Failed to get response.</p>
                        )}
                        <div ref={bottomRef} />
                    </div>
                    <div className="px-4 py-3 border-t border-slate-700 shrink-0">
                        <div className="flex gap-2">
                            <input
                                value={input}
                                onChange={e => setInput(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage() } }}
                                placeholder="Ask about this pod…"
                                className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-violet-500"
                            />
                            <button
                                onClick={sendMessage}
                                disabled={!input.trim() || aiMutation.isPending}
                                className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-500 disabled:opacity-40 transition-colors"
                            >
                                Send
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="flex-1 overflow-y-auto p-4 font-mono text-xs leading-relaxed">
                    {logsQuery.isLoading && (
                        <p className="text-slate-500 animate-pulse">Loading logs…</p>
                    )}
                    {logsQuery.isError && (
                        <p className="text-red-400">Failed to load logs.</p>
                    )}
                    {logsQuery.data && (
                        <pre className="text-slate-300 whitespace-pre-wrap break-all">
                            {logsQuery.data}
                        </pre>
                    )}
                </div>
            )}
        </div>
    )
}

// ---- Cluster map ----

function PodMetricBar({ pct, colorClass }: { pct: number; colorClass: string }) {
    const barColor = pct >= 85 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-400' : colorClass
    return (
        <div className="w-6 h-1 bg-slate-700 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
        </div>
    )
}

interface PodDotProps {
    pod: PodSnapshot
    namespace: string
    onClick: (selected: SelectedPod) => void
    delay: number
    visible: boolean
    cpuPct?: number | undefined
    memPct?: number | undefined
}

function PodDot({ pod, namespace, onClick, delay, visible, cpuPct, memPct }: PodDotProps) {
    const colors = podStatusColor(pod)
    const hasMetrics = cpuPct !== undefined && memPct !== undefined
    return (
        <div
            className="flex flex-col items-center gap-0.5 transition-all duration-500"
            style={{
                transitionDelay: `${delay}ms`,
                opacity: visible ? 1 : 0,
                transform: visible ? 'scale(1)' : 'scale(0)',
            }}
        >
            <button
                onClick={() => onClick({ namespace, pod })}
                title={`${pod.name}\nStatus: ${pod.status}\nRestarts: ${pod.restartCount}${pod.image ? `\nImage: ${pod.image}` : ''}`}
                className={`${hasMetrics ? 'h-3 w-3' : 'h-4 w-4'} rounded-full ring-2 ${colors.dot} ${colors.ring} hover:scale-125 hover:brightness-110 cursor-pointer transition-transform`}
            />
            {hasMetrics && (
                <>
                    <PodMetricBar pct={cpuPct} colorClass="bg-sky-400" />
                    <PodMetricBar pct={memPct} colorClass="bg-violet-400" />
                </>
            )}
        </div>
    )
}

interface DeploymentRowProps {
    dep: DeploymentSnapshot
    namespace: string
    onPodClick: (selected: SelectedPod) => void
    delay: number
    visible: boolean
    podPctByKey?: Map<string, { cpu: number; mem: number }> | undefined
    hpa?: HpaInfo | undefined
}

function DeploymentRow({ dep, namespace, onPodClick, delay, visible, podPctByKey, hpa }: DeploymentRowProps) {
    const queryClient = useQueryClient()
    const [confirm, setConfirm] = useState(false)

    const restartMutation = useMutation({
        mutationFn: () => releaseApi.restartDeploymentPods(namespace, dep.name),
        onSuccess: () => {
            setConfirm(false)
            void queryClient.invalidateQueries({ queryKey: ['cluster'] })
        },
    })

    const status = deployStatus(dep)
    const styles = STATUS_STYLES[status]

    return (
        <div
            className="group flex items-center gap-3 py-2 border-b border-slate-700/30 last:border-0 transition-all duration-500"
            style={{
                transitionDelay: `${delay}ms`,
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateX(0)' : 'translateX(-12px)',
            }}
        >
            <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${styles.dot}`} />
            <span className="text-xs font-mono text-slate-300 w-28 shrink-0 truncate">{dep.name}</span>
            <div className="flex flex-col shrink-0 w-10">
                <span className="text-[10px] text-slate-600">{dep.readyReplicas}/{dep.desiredReplicas}</span>
                {hpa && (
                    <span className="text-[9px] text-slate-600 whitespace-nowrap">
                        {hpa.minReplicas}–{hpa.maxReplicas}{hpa.targetCpuPercent != null ? `@${hpa.targetCpuPercent}%` : ''}
                    </span>
                )}
            </div>
            <div className="flex items-end gap-1.5 flex-wrap flex-1">
                {dep.pods.map((pod, i) => {
                    const pct = podPctByKey?.get(`${namespace}/${pod.name}`)
                    return (
                        <PodDot
                            key={pod.name}
                            pod={pod}
                            namespace={namespace}
                            onClick={onPodClick}
                            delay={delay + 50 + i * 40}
                            visible={visible}
                            cpuPct={pct?.cpu}
                            memPct={pct?.mem}
                        />
                    )
                })}
            </div>
            {/* Restart pods */}
            {confirm ? (
                <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-[10px] text-amber-400">Restart pods?</span>
                    <button
                        onClick={() => restartMutation.mutate()}
                        disabled={restartMutation.isPending}
                        className="text-[10px] px-2 py-0.5 rounded bg-red-600/80 hover:bg-red-500 text-white disabled:opacity-50 transition-colors"
                    >
                        {restartMutation.isPending ? '…' : 'Yes'}
                    </button>
                    <button
                        onClick={() => setConfirm(false)}
                        className="text-[10px] px-2 py-0.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors"
                    >
                        No
                    </button>
                </div>
            ) : (
                <button
                    onClick={() => setConfirm(true)}
                    title="Restart pods (delete pods, controller reschedules)"
                    className="shrink-0 text-[10px] px-2 py-0.5 rounded border border-slate-700 text-slate-500 hover:border-slate-500 hover:text-slate-300 transition-colors opacity-0 group-hover:opacity-100"
                >
                    ↺ Restart
                </button>
            )}
            {restartMutation.isError && (
                <span className="text-[10px] text-red-400 shrink-0">Failed</span>
            )}
        </div>
    )
}

// ---- Node chip ----

function NodeChip({ node, metric }: { node: import('../api').NodeInfo; metric?: NodeMetric | undefined }) {
    const queryClient = useQueryClient()
    const [action, setAction] = useState<'drain' | 'restart' | null>(null)

    const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['cluster'] })

    const drainMutation = useMutation({
        mutationFn: () => releaseApi.drainNode(node.name),
        onSuccess: () => { setAction(null); invalidate() },
    })

    const restartMutation = useMutation({
        mutationFn: () => releaseApi.restartNode(node.name),
        onSuccess: () => { setAction(null); invalidate() },
    })

    const uncordonMutation = useMutation({
        mutationFn: () => releaseApi.uncordonNode(node.name),
        onSuccess: invalidate,
    })

    const isPending = drainMutation.isPending || restartMutation.isPending || uncordonMutation.isPending
    const isError   = drainMutation.isError   || restartMutation.isError   || uncordonMutation.isError

    const shortName = node.name.split('-').slice(-1)[0]

    return (
        <div className="rounded-lg bg-slate-700/50 border border-slate-700 px-3 py-2 flex flex-col gap-1.5 min-w-[140px]">
            {/* Header row */}
            <div className="flex items-center gap-1.5">
                <span className={`h-2 w-2 rounded-full shrink-0 ${node.status === 'Ready' ? 'bg-emerald-400' : 'bg-red-500'}`} />
                <span className="text-xs font-mono text-slate-300 flex-1 truncate">{shortName}</span>
                <span className="text-[10px] text-slate-500">{node.role}</span>
                {node.unschedulable && (
                    <span className="text-[10px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-400">cordoned</span>
                )}
            </div>

            {/* Inline CPU / memory bars */}
            {metric && (
                <div className="space-y-1">
                    <div>
                        <div className="text-[9px] text-slate-600 mb-0.5">CPU</div>
                        <MetricsBar used={metric.cpuMillicores} capacity={metric.cpuCapacityMillicores} colorClass="bg-sky-400" />
                    </div>
                    <div>
                        <div className="text-[9px] text-slate-600 mb-0.5">Mem</div>
                        <MetricsBar used={metric.memoryMiB} capacity={metric.memoryCapacityMiB} colorClass="bg-violet-400" />
                    </div>
                </div>
            )}

            {/* Action row — switches based on cordoned state */}
            {node.unschedulable ? (
                /* Cordoned: show only Uncordon (safe, no confirm needed) */
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => uncordonMutation.mutate()}
                        disabled={isPending}
                        title="Allow scheduler to place pods on this node again"
                        className="text-[10px] px-1.5 py-0.5 rounded border border-emerald-700/50 text-emerald-400 hover:border-emerald-500 hover:text-emerald-300 disabled:opacity-50 transition-colors"
                    >
                        {uncordonMutation.isPending ? '…' : 'Uncordon'}
                    </button>
                    {isError && <span className="text-[10px] text-red-400">Failed</span>}
                </div>
            ) : action !== null ? (
                /* Drain / Restart confirm prompt */
                <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-amber-400">{action === 'drain' ? 'Drain?' : 'Restart VM?'}</span>
                    <button
                        onClick={() => action === 'drain' ? drainMutation.mutate() : restartMutation.mutate()}
                        disabled={isPending}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-red-600/80 hover:bg-red-500 text-white disabled:opacity-50 transition-colors"
                    >
                        {isPending ? '…' : 'Yes'}
                    </button>
                    <button
                        onClick={() => setAction(null)}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-slate-600 hover:bg-slate-500 text-slate-300 transition-colors"
                    >
                        No
                    </button>
                </div>
            ) : (
                /* Normal: Drain + Restart */
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => setAction('drain')}
                        title="Cordon node and evict pods"
                        className="text-[10px] px-1.5 py-0.5 rounded border border-slate-600 text-slate-400 hover:border-amber-600/60 hover:text-amber-400 transition-colors"
                    >
                        Drain
                    </button>
                    <button
                        onClick={() => setAction('restart')}
                        title="Drain then restart the Azure VM"
                        className="text-[10px] px-1.5 py-0.5 rounded border border-slate-600 text-slate-400 hover:border-red-600/60 hover:text-red-400 transition-colors"
                    >
                        Restart
                    </button>
                    {isError && <span className="text-[10px] text-red-400">Failed</span>}
                </div>
            )}
        </div>
    )
}

// ---- Node metrics ----

function MetricsBar({ used, capacity, colorClass }: { used: number; capacity: number; colorClass: string }) {
    const pct = capacity > 0 ? Math.min(100, Math.round((used / capacity) * 100)) : 0
    const barColor = pct >= 85 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-400' : colorClass
    return (
        <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="text-[10px] text-slate-500 w-7 text-right">{pct}%</span>
        </div>
    )
}


// ---- SVG connector lines ----

function ConnectorSvg({ nsCount }: { nsCount: number }) {
    const W = 800
    const originX = W / 2
    const originY = 78
    const targetY = 140
    const spacing = nsCount > 0 ? Math.min(220, (W - 80) / nsCount) : 220
    const startX = originX - ((nsCount - 1) / 2) * spacing

    return (
        <svg
            className="absolute top-0 left-0 w-full pointer-events-none"
            style={{ height: targetY + 8 }}
            viewBox={`0 0 ${W} ${targetY + 8}`}
            preserveAspectRatio="xMidYMid meet"
        >
            {Array.from({ length: nsCount }, (_, i) => {
                const tx = startX + i * spacing
                return (
                    <path
                        key={i}
                        d={`M ${originX} ${originY} C ${originX} ${(originY + targetY) / 2}, ${tx} ${(originY + targetY) / 2}, ${tx} ${targetY}`}
                        fill="none"
                        stroke="rgb(51 65 85)"
                        strokeWidth="1.5"
                        strokeDasharray="4 3"
                    />
                )
            })}
        </svg>
    )
}

// ---- Main page ----

export function ClusterPage() {
    const [visible, setVisible] = useState(false)
    const [selectedPod, setSelectedPod] = useState<SelectedPod | null>(null)

    const { data, isLoading, isError, error, dataUpdatedAt, refetch, isFetching } = useQuery({
        queryKey: ['cluster'],
        queryFn: releaseApi.getCluster,
        refetchInterval: 30_000,
    })

    const { data: metricsData } = useQuery({
        queryKey: ['node-metrics'],
        queryFn: releaseApi.getNodeMetrics,
        refetchInterval: 30_000,
        staleTime: 20_000,
    })

    const { data: podMetricsData } = useQuery({
        queryKey: ['pod-metrics'],
        queryFn: releaseApi.getPodMetrics,
        refetchInterval: 30_000,
        staleTime: 20_000,
    })

    const { data: hpaData } = useQuery({
        queryKey: ['hpa'],
        queryFn: releaseApi.getHpa,
        refetchInterval: 60_000,
        staleTime: 50_000,
    })

    const podPctByKey = useMemo(() => {
        if (!podMetricsData || !metricsData) return new Map<string, { cpu: number; mem: number }>()
        const nodeByName = new Map(metricsData.nodes.map(n => [n.nodeName, n]))
        const map = new Map<string, { cpu: number; mem: number }>()
        for (const pm of podMetricsData.pods) {
            const node = nodeByName.get(pm.nodeName)
            if (node && node.cpuCapacityMillicores > 0 && node.memoryCapacityMiB > 0) {
                map.set(`${pm.namespace}/${pm.podName}`, {
                    cpu: Math.min(100, Math.round((pm.cpuMillicores / node.cpuCapacityMillicores) * 100)),
                    mem: Math.min(100, Math.round((pm.memoryMiB / node.memoryCapacityMiB) * 100)),
                })
            }
        }
        return map
    }, [podMetricsData, metricsData])

    const hpaByDeployment = useMemo(() => {
        const map = new Map<string, HpaInfo>()
        for (const hpa of hpaData?.hpas ?? []) {
            map.set(`${hpa.namespace}/${hpa.deploymentName}`, hpa)
        }
        return map
    }, [hpaData])

    useEffect(() => {
        if (data) {
            const t = setTimeout(() => setVisible(true), 80)
            return () => clearTimeout(t)
        }
        return undefined
    }, [data])

    return (
        <div className={`flex flex-col min-h-0 flex-1 ${selectedPod ? 'mr-[640px]' : ''} transition-all duration-300`}>
            <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
                    <div>
                        <span className="text-sm font-semibold text-slate-200">Cluster</span>
                        <PageDescription subtitle={data ? data.clusterName : 'Live cluster state'} storageKey="desc-cluster" detail="Live view of the AKS cluster: namespaces, deployments, pod health, and node metrics. Click a pod dot to inspect logs and get AI analysis. Use the Restart button on a deployment to roll its pods." />
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                        {dataUpdatedAt > 0 && (
                            <span>Updated {new Date(dataUpdatedAt).toLocaleTimeString()}</span>
                        )}
                        <button
                            onClick={() => void refetch()}
                            disabled={isFetching}
                            className="rounded-lg border border-slate-700 px-3 py-1.5 text-slate-300 hover:bg-slate-800 hover:text-white transition-colors disabled:opacity-40"
                        >
                            {isFetching ? 'Refreshing…' : 'Refresh'}
                        </button>
                    </div>
                </div>
            </header>

            <main className="flex-1 overflow-y-auto px-4 sm:px-6 py-8">
                {isLoading && (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-slate-500 text-sm animate-pulse">Fetching cluster state…</div>
                    </div>
                )}

                {isError && (
                    <div className="rounded-xl border border-red-800 bg-red-950/50 p-6 text-red-300 text-sm max-w-2xl space-y-1">
                        <p className="font-medium">Failed to load cluster data</p>
                        {error instanceof Error && (
                            <p className="font-mono text-xs text-red-400 break-all">{error.message}</p>
                        )}
                    </div>
                )}

                {data && (
                    <div className="max-w-5xl mx-auto space-y-8">
                        {/* Cluster + node overview */}
                        <div
                            className="transition-all duration-600"
                            style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(-16px)' }}
                        >
                            <div className="rounded-xl border border-slate-700 bg-slate-800/60 px-4 sm:px-6 py-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <svg className="h-5 w-5 text-slate-400" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <rect x="2" y="6" width="16" height="8" rx="2" />
                                        <circle cx="5.5" cy="10" r="1" fill="currentColor" stroke="none" />
                                        <path d="M8 10h6" strokeLinecap="round" />
                                    </svg>
                                    <div>
                                        <div className="text-base font-semibold text-white">{data.clusterName}</div>
                                        <div className="text-xs text-slate-500">{data.nodes.length} node{data.nodes.length !== 1 ? 's' : ''}</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    {data.nodes.map(node => (
                                        <NodeChip
                                            key={node.name}
                                            node={node}
                                            metric={metricsData?.nodes.find(m => m.nodeName === node.name)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Namespace columns */}
                        <div className="relative">
                            <ConnectorSvg nsCount={data.namespaces.length} />
                            <div className="pt-[148px] grid gap-4" style={{ gridTemplateColumns: `repeat(${data.namespaces.length}, 1fr)` }}>
                                {data.namespaces.map((ns, ni) => {
                                    const status = nsStatus(ns)
                                    const styles = STATUS_STYLES[status]
                                    const nsDelay = 200 + ni * 120

                                    return (
                                        <div
                                            key={ns.name}
                                            className="rounded-xl border border-slate-700 bg-slate-800/40 overflow-hidden transition-all duration-500"
                                            style={{
                                                transitionDelay: `${nsDelay}ms`,
                                                opacity: visible ? 1 : 0,
                                                transform: visible ? 'translateY(0)' : 'translateY(20px)',
                                            }}
                                        >
                                            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/50 bg-slate-800/60">
                                                <span className="text-sm font-semibold text-white">{ns.name}</span>
                                                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${styles.badge}`}>
                                                    <span className={`h-1.5 w-1.5 rounded-full ${styles.dot}`} />
                                                    {status}
                                                </span>
                                            </div>
                                            <div className="px-4">
                                                {ns.deployments.map((dep, di) => (
                                                    <DeploymentRow
                                                        key={dep.name}
                                                        dep={dep}
                                                        namespace={ns.name}
                                                        onPodClick={setSelectedPod}
                                                        delay={nsDelay + 80 + di * 60}
                                                        visible={visible}
                                                        podPctByKey={podPctByKey}
                                                        hpa={hpaByDeployment.get(`${ns.name}/${dep.name}`)}
                                                    />
                                                ))}
                                                {ns.deployments.length === 0 && (
                                                    <p className="text-xs text-slate-600 py-4 text-center">No deployments</p>
                                                )}
                                            </div>
                                            <div className="px-4 py-2 text-[10px] text-slate-600">
                                                {ns.deployments.reduce((s, d) => s + d.pods.length, 0)} pods
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>

                        {/* Legend */}
                        <div
                            className="flex items-center gap-6 text-xs text-slate-500 transition-all duration-500"
                            style={{
                                transitionDelay: '600ms',
                                opacity: visible ? 1 : 0,
                            }}
                        >
                            <span className="font-medium text-slate-400">Pod status:</span>
                            {[
                                { dot: 'bg-emerald-400', label: 'Running' },
                                { dot: 'bg-amber-400',   label: 'Pending' },
                                { dot: 'bg-red-500',     label: 'CrashLoopBackOff' },
                                { dot: 'bg-slate-500',   label: 'Unknown' },
                            ].map(({ dot, label }) => (
                                <span key={label} className="flex items-center gap-1.5">
                                    <span className={`h-2.5 w-2.5 rounded-full ${dot}`} />
                                    {label}
                                </span>
                            ))}
                            <span className="text-slate-600 ml-auto">Click a pod dot to view logs and troubleshoot</span>
                        </div>
                    </div>
                )}
            </main>

            {selectedPod && (
                <PodLogViewer
                    selected={selectedPod}
                    onClose={() => setSelectedPod(null)}
                />
            )}
        </div>
    )
}
